import * as React from 'react';
import { Container, IconButton, LinearProgress, Paper, Table, TableBody, TableContainer, Toolbar, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../stores';
import { useOpenDialog } from '../../../hooks/useOpenDialog';
import { TerritoryGroup } from '../../../domain/TerritoryGroup';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import TerritoryGroupTableRow from './TerritoryGroupTableRow';
import { Territory } from '../../../domain/Territory';
import AssignTerritoryDialog from '../dialogs/AssignTerritoryDialog';
import ShowTerritoryDialog from '../dialogs/ShowTerritoryDialog';
import TerritoriesTable from './TerritoriesTable';
import { TerritoryAssignment } from '../../../domain/TerritoryAssignment';
import EditAssignmentDialog from '../dialogs/EditAssignmentDialog';
import { useTranslation } from 'react-i18next';
import SearchTerritoriesModal from './SearchTerritoriesModal';
import FilterTerritoriesModal from './FilterTerritoriesModal';


const MobileTerritoriesManagementTab = observer(() => {
    const { t } = useTranslation();
    const { serverStore, territoryStore } = useStores();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const [selectedTerritory, setSelectedTerritory] = React.useState<Territory | undefined>();
    const [selectedAssignment, setSelectedAssignment] = React.useState<TerritoryAssignment | undefined>();

    React.useEffect(() => {
        if (territoryStore.groups.length === 0) {
            territoryStore.getAllGroups();
        }
    }, [territoryStore]);


    const handleCloseDialog = () => {
        closeDialog();
        setSelectedAssignment(undefined);
        setSelectedTerritory(undefined);
    }

    const openEditAssignmentDialog = (assignment: TerritoryAssignment) => {
        setSelectedAssignment(assignment);
        openDialog("edit-assignment");
    }

    const openAssignTerritoryDialog = (territory: Territory) => {
        setSelectedTerritory(territory);
        openDialog("assign-territory")
    }

    const showTerritoryDialog = (territory: Territory) => {
        setSelectedTerritory(territory);
        openDialog("show-territory")
    }

    const handleSearchSubmit = (search: string) => {
        if (search === "") {
            territoryStore.getAllGroups();
        } else {
            territoryStore.searchGroups(search);
        }
    }

    const handleFilterChange = (filter: string) => {
        if (filter === "all") {
            territoryStore.getAllGroups();
        } else {
            territoryStore.getAllGroups(filter);
        }
    }

    return (
        <>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                {serverStore.serverRequestLoading && <LinearProgress />}
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }} >
                    <Toolbar>
                        <Typography sx={{ flex: '1 1 100%' }} variant="h6" component="div">
                            {t("territories")}
                        </Typography>
                        <IconButton onClick={() => openDialog("search-territories")}>
                            <SearchIcon />
                        </IconButton>
                        <IconButton onClick={() => openDialog("filter-territories")}>
                            <FilterListIcon />
                        </IconButton>
                    </Toolbar>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                {territoryStore.groups && territoryStore.groups
                                    .map((group: TerritoryGroup) => (
                                        <TerritoryGroupTableRow
                                            key={group.id}
                                            editable={false}
                                            group={group}
                                        >
                                            <TerritoriesTable
                                                territories={group.territories || []}
                                                onTerritoryAssign={openAssignTerritoryDialog}
                                                onTerritoryFocus={showTerritoryDialog}
                                            />
                                        </TerritoryGroupTableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Container>
            <AssignTerritoryDialog
                territory={selectedTerritory!}
                isOpen={isOpen("assign-territory")}
                onAssignmentEdit={openEditAssignmentDialog}
                onClose={handleCloseDialog}
            />
            <ShowTerritoryDialog
                territory={selectedTerritory!}
                isOpen={isOpen("show-territory")}
                onClose={handleCloseDialog}
                onAssign={openAssignTerritoryDialog}
            />
            <EditAssignmentDialog
                assignment={selectedAssignment!}
                isOpen={isOpen("edit-assignment")}
                onClose={handleCloseDialog}
            />
            <SearchTerritoriesModal
                isOpen={isOpen("search-territories")}
                onClose={handleCloseDialog}
                onSearchSubmit={handleSearchSubmit}
            />
            <FilterTerritoriesModal
                isOpen={isOpen("filter-territories")}
                onClose={handleCloseDialog}
                onFilterChange={handleFilterChange}
            />
        </ >
    );
});

export default MobileTerritoriesManagementTab;